import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_COST_DEFAULT } from '../../../../app/constants/paths.constant';
import type { ReferencesContext_GenericStatus } from '../../../../contexts/ReferencesContext';

import type { ResolvableServicePage_CostUsagesList_CostUsage } from '.';

export const COST_STATUS_ID = 'costStatus';

interface Props {
  readonly costUsage: Readonly<ResolvableServicePage_CostUsagesList_CostUsage>;
}

const Action = ({ costUsage }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_COST_DEFAULT, { id: costUsage.cost._id });
    void navigate(path);
  }, [navigate, costUsage.cost._id]);

  const actionLabel = 'Consulter le Coût';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<
  Readonly<ResolvableServicePage_CostUsagesList_CostUsage>
>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (
      params: Readonly<
        GridRowParams<Readonly<ResolvableServicePage_CostUsagesList_CostUsage>>
      >,
    ) => [<Action costUsage={params.row} key="consult" />],
  },
  {
    field: 'costCode',
    headerName: 'Code',
    width: 200,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_CostUsagesList_CostUsage>,
    ) => row.cost.code,
  },
  {
    field: 'costLabel',
    headerName: 'Libellé',
    width: 400,
    description: 'Libellé',
    editable: false,
    sortable: true,
    type: 'string',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_CostUsagesList_CostUsage>,
    ) => row.cost.label,
  },
  {
    field:
      nameof<Readonly<ResolvableServicePage_CostUsagesList_CostUsage>>(
        'quantity',
      ),
    headerName: 'Quantité',
    width: 200,
    description: 'Quantité',
    editable: false,
    sortable: true,
    type: 'number',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_CostUsagesList_CostUsage>,
    ) => row.quantity.value,
  },
  {
    field: COST_STATUS_ID,
    headerName: 'Statut',
    width: 200,
    description: 'Statut',
    editable: false,
    sortable: true,
    hideable: true,
    type: 'singleSelect',
    valueGetter: (
      _: unknown,
      row: Readonly<ResolvableServicePage_CostUsagesList_CostUsage>,
    ) =>
      genericStatuses.find(
        (genericStatus) => genericStatus._id === row.cost.statusId,
      )?.code ?? 'Inconnu',
    valueOptions: genericStatuses.map((genericStatus) => ({
      value: genericStatus.code,
      label: genericStatus.label,
    })),
  },
];
